import supabase from '../supabase/supabase';
import { storageService } from './storageService';
import { fetchAllPages } from './paginatedFetch';

const PROFILE_FIELDS = [
  'name', 'address', 'lat', 'lng', 'aboutMe', 'avatarUrl', 'avatar_url', 'gender',
  'householdSize', 'profession', 'incomeRange', 'birthDate',
];

const pickProfileFields = (input = {}) => PROFILE_FIELDS.reduce((result, key) => {
  if (Object.prototype.hasOwnProperty.call(input, key)) result[key] = input[key];
  return result;
}, {});

export const userService = {
  async register(email, password) {
    const { data: authData, error: authError } = await supabase.auth.signUp({ email, password });
    if (authError) throw authError;
    if (!authData.user) throw new Error('Registrierung fehlgeschlagen. Möglicherweise existiert die E-Mail bereits.');

    const user = { id: authData.user.id, name: '', email, address: '' };
    const { data, error } = await supabase.from('users').insert([user]).select().single();
    if (error) {
      console.warn('Profil konnte nicht sofort angelegt werden:', error);
      return user;
    }
    return data;
  },

  async login(email, password) {
    const { data: authData, error: authError } = await supabase.auth.signInWithPassword({ email, password });
    if (authError) throw authError;
    return this.getProfile(authData.user.id);
  },

  async getProfile(userId) {
    const { data, error } = await supabase.from('users').select('*').eq('id', userId).single();
    if (error) {
      if (error.code === 'PGRST116') {
        const { data: authUser } = await supabase.auth.getUser();
        if (authUser?.user?.id === userId) return this.createProfile(userId, '', authUser.user.email);
      }
      throw error;
    }
    return data;
  },

  async createProfile(id, name, email) {
    const newUser = {
      id,
      name: name || '',
      email: email || '',
      avatarUrl: `https://api.dicebear.com/7.x/avataaars/svg?seed=${id}`,
      address: '',
      role: 'user',
    };
    const { data, error } = await supabase.from('users').insert([newUser]).select().single();
    if (error) throw error;
    return data;
  },

  async getPublicUsers(options = {}) {
    return fetchAllPages(
      () => supabase.from('public_profiles')
        .select('id,name,avatarUrl,aboutMe,role,created_at')
        .order('created_at', { ascending: false }),
      { ...options, label: 'Öffentliche Profile' },
    );
  },

  async getAllUsers() {
    const { data, error } = await supabase.rpc('admin_list_user_details');
    if (error) throw error;
    return data || [];
  },

  async updateProfile(id, dataToUpdate) {
    const previous = await this.getProfile(id);
    const submittedAvatar = dataToUpdate.avatarUrl || dataToUpdate.avatar_url;
    const avatarUrl = storageService.isInlineImage(submittedAvatar)
      ? await storageService.uploadDataUrl(submittedAvatar, { userId: id, folder: 'avatars' })
      : submittedAvatar;

    const payload = {
      ...pickProfileFields(dataToUpdate),
      ...(avatarUrl !== undefined ? { avatarUrl } : {}),
      updated_at: new Date().toISOString(),
    };
    delete payload.avatar_url;

    const { data, error } = await supabase.from('users').update(payload).eq('id', id).select().single();
    if (error) {
      if (storageService.isInlineImage(submittedAvatar) && avatarUrl) {
        storageService.removePublicUrl(avatarUrl).catch(() => undefined);
      }
      throw error;
    }

    const previousAvatar = previous?.avatarUrl || previous?.avatar_url;
    if (storageService.isInlineImage(submittedAvatar) && previousAvatar && previousAvatar !== avatarUrl) {
      storageService.removePublicUrl(previousAvatar).catch((storageError) => console.warn('Alter Avatar konnte nicht gelöscht werden:', storageError));
    }
    return data;
  },
};
